import { InformationCircleIcon } from "@heroicons/react/16/solid"
import type { NetworkOptions } from "@src/components/DefuseSDK/hooks/useNetworkLists"
import ListItem from "@src/components/ListItem"
import type { ReactNode } from "react"
import type { ContactNetwork } from "../../features/contacts/utils/contactNetwork"
import type { SupportedChainName } from "../../types/base"
import {
  isValidBlockchainEnumKey,
  reverseAssetNetworkAdapter,
} from "../../utils/adapters"
import { isAuroraVirtualChain } from "../../utils/blockchain"
import InfoPopover from "../InfoPopover"
import { PoweredByAuroraLabel } from "../PoweredByAuroraLabel"

interface NetworkListProps {
  title?: string
  additionalInfo?: string
  networkOptions: NetworkOptions
  selectedNetwork: ContactNetwork | null
  onChangeNetwork: (network: SupportedChainName) => void
  disabled?: boolean
  renderValueDetails?: (address: string) => ReactNode
}

export const NetworkList = ({
  title,
  additionalInfo,
  networkOptions,
  selectedNetwork,
  onChangeNetwork,
  disabled = false,
  renderValueDetails,
}: NetworkListProps) => {
  const hasStandardNetworks = Object.entries(networkOptions).some(
    ([network, networkInfo]) =>
      networkInfo != null && isValidBlockchainEnumKey(network)
  )

  if (!hasStandardNetworks) return null

  return (
    <div className="mt-1 space-y-2">
      {(title || additionalInfo) && (
        <div className="flex items-center gap-2">
          {title && (
            <h3 className="font-medium text-gray-500 text-sm/6 dark:text-gray-400">
              {title}
            </h3>
          )}

          {additionalInfo && (
            <InfoPopover>
              <InfoPopover.Trigger>
                <button
                  type="button"
                  className="relative flex size-6 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 data-popup-open:bg-gray-100 data-popup-open:text-gray-700 dark:text-gray-400 dark:data-popup-open:bg-white/5 dark:data-popup-open:text-white dark:hover:bg-white/5 dark:hover:text-white"
                  aria-label={additionalInfo}
                >
                  <span
                    className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 pointer-fine:hidden size-8"
                    aria-hidden="true"
                  />
                  <InformationCircleIcon className="size-4" />
                </button>
              </InfoPopover.Trigger>
              <InfoPopover.Content
                side="bottom"
                className="max-w-56 text-balance text-center"
              >
                {additionalInfo}
              </InfoPopover.Content>
            </InfoPopover>
          )}
        </div>
      )}
      <div className="space-y-1">
        {Object.keys(networkOptions).map((network) => {
          const networkInfo = networkOptions[network]

          if (!networkInfo) return null

          if (!isValidBlockchainEnumKey(network)) {
            return null
          }

          const networkName = reverseAssetNetworkAdapter[network]

          return (
            <ListItem
              key={networkInfo.value}
              disabled={disabled}
              onClick={() => onChangeNetwork(networkName)}
              highlight={selectedNetwork === networkName}
              dataTestId={`network-row-${networkInfo.label}`}
            >
              {networkInfo.icon}
              <ListItem.Content>
                <ListItem.Title>{networkInfo.label}</ListItem.Title>
              </ListItem.Content>
              <ListItem.Content align="end">
                {renderValueDetails?.(networkInfo.value)}
                {isAuroraVirtualChain(networkName) && <PoweredByAuroraLabel />}
              </ListItem.Content>
            </ListItem>
          )
        })}
      </div>
    </div>
  )
}
